/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Adam (Tuannd@rsupport.com)
 *
 * @description
 *
 * @created at Thu Oct 24 2024
 **/

import { useEffect, useState } from "react";

import { useCommon } from "@/frameworks/core/API";
import {
	Button,
	Column,
	Loader,
	TabContainer,
	TabItem,
	Typography
} from "@/frameworks/designStuff/components";
import { useProperties } from "@/frameworks/libraries/properties";

import { Each, Maybe } from "@/frameworks/libraries/components";
import * as S from "./private/emotion";
import { _T } from "@rfice/language";

const itemsPerPage = 5;

export default function ProfileImagesPopover({
	onSelectProfileImg,
	currentProfileImage
}: {
	onSelectProfileImg?: (profileImg: { id: string; path?: string }) => void;
	currentProfileImage: string;
}) {
	const properties = useProperties();
	const [profileImageList, setProfileImageList] = useState<WebServices.Default.ProfileImage.List.Data>();
	const [pageState, setPageState] = useState(0);
	const [profileImgPaginateArray, setProfileImgPaginateArray] = useState<
		WebServices.Default.ProfileImage.List.Data[]
	>([]);

	const commonAPI = useCommon({
		onSuccess() {
			const { data } = commonAPI.profileImage.list.result;
			if (data) {
				setProfileImageList((prev) => {
					if (!prev) return data;
					return [...prev, ...data];
				});
			}
		}
	});

	const profileImgPaginate = () => {
		if (!profileImageList) return [];
		const paginatedArray: WebServices.Default.ProfileImage.List.Data[] = [];
		for (let i = 0; i < profileImageList.length; i += itemsPerPage) {
			const page = profileImageList.slice(i, i + itemsPerPage);
			paginatedArray.push(page);
		}
		return paginatedArray;
	};

	const handleNext = () => {
		setPageState(pageState + 1);
	};

	const handlePrev = () => {
		setPageState(pageState - 1);
	};

	useEffect(() => {
		commonAPI.profileImage.list.execute();
	}, []);

	useEffect(() => {
		if (!profileImageList) return;
		setProfileImgPaginateArray(profileImgPaginate());
	}, [profileImageList]);

	return (
		<S.Wrapper>
			<div style={{ width: "100%", height: "100%" }}>
				<TabContainer
					activeId={
						profileImgPaginateArray && profileImgPaginateArray[pageState]
							? profileImgPaginateArray[pageState][0]?.id
							: "profile-a"
					}
					allActive={true}
				>
					<S.RepresentativeWrapper>
						<S.WrapperTab>
							{profileImgPaginateArray[pageState]?.map((profile) => (
								<TabItem id={profile.id} key={profile.id} style={{ padding: "0 8px 0 0" }}>
									<S.RepresentativeImg
										src={`${properties.default.webDomain}${profile?.imageFiles?.length ? profile?.imageFiles[0]?.path : ""}`}
										alt="profile-img"
									/>
								</TabItem>
							))}
						</S.WrapperTab>
						<S.Action>
							{pageState > 0 && (
								<Button.Icon name="Left24" size="large" filled="transparent" onClick={handlePrev} />
							)}
							{pageState < profileImgPaginateArray.length - 1 && (
								<Button.Icon name="Right24" size="large" filled="transparent" onClick={handleNext} />
							)}
						</S.Action>
					</S.RepresentativeWrapper>
					<Maybe
						test={commonAPI.profileImage.list.isLoading}
						fallback={
							<Each
								of={profileImageList}
								render={(profile) => (
									<S.WrapperTabContent activeId={profile.id} key={profile.id}>
										<Maybe
											test={profile.imageFiles?.length}
											fallback={
												<Column
													horizonAlign="center"
													verticalAlign="center"
													style={{ width: "100%", height: "100%" }}
												>
													<Typography>{_T("기록 없음")}</Typography>
												</Column>
											}
										>
											<S.ProfileImgList>
												<Each
													of={profile.imageFiles}
													render={(image: { id: string; path: string }) => (
														<S.ProfileImg
															key={image.id}
															isUsingImage={Boolean(currentProfileImage === image?.path)}
															onClick={() => onSelectProfileImg?.(image)}
															src={`${properties.default.webDomain}${image?.path}`}
															alt="profile img item"
														/>
													)}
												/>
											</S.ProfileImgList>
										</Maybe>
									</S.WrapperTabContent>
								)}
							/>
						}
					>
						<Column style={{ width: "100%", height: "100%" }} horizonAlign="center" verticalAlign="center">
							<Loader />
						</Column>
					</Maybe>
				</TabContainer>
			</div>
		</S.Wrapper>
	);
}
